import Decimal from 'decimal.js';

export * from './ai.types';
export * from './bills';
export * from './edi.types';
export * from './labels';
export * from './notifications.types';

export type Organization = {
  id: number;
  logoUrl: string;
  label: string;
  primaryLocation?: Place;
  places: Place[];
  fragmentLedgerId: string;
  salesOrderDocumentPrefix?: string;
  productDescriptionTemplate?: string;
};

export type Membership = { user?: User; userId?: number; subsidiary?: Place };

export type User = { firstName: string; lastName: string; email: string };

export type Product = {
  id: number;
  productCode?: string;

  commodityId: number;
  commodity: {
    receivablesAccount?: LedgerAccount;
    payablesAccount?: LedgerAccount;
    incomeAccount?: LedgerAccount;
    expenseAccount?: LedgerAccount;
    name: string;
  };
  commodityStyle: { name: string };
  commoditySize: { name: string };
  commodityUnit: { name: string };
  commodityLabel?: { name: string };

  countryOfOrigin: string;
  organic?: boolean;
  grade?: string;

  weightPerInventoryUnit: number | Decimal;
  variableWeight: boolean;

  cartonsPerPallet: number;

  receivablesAccount?: LedgerAccount;
  payablesAccount?: LedgerAccount;
  incomeAccount?: LedgerAccount;
  expenseAccount?: LedgerAccount;
  customFields?: CustomFields;
};

export type Lot = {
  slug: string;
  initialQuantity: number;
  initialWeight: number;
  product: Product;
  quantity: number;
  weight?: number;
  palletTags?: PalletTag[];
  grade?: string;
};

export type BusinessEntity = {
  id: number;
  name: string;
  receivablesAccount?: LedgerAccount;
  payablesAccount?: LedgerAccount;
  incomeAccount?: LedgerAccount;
  expenseAccount?: LedgerAccount;
  places?: Place[];
  customFields?: CustomFields;
};

export type LedgerEntryGroupKey =
  | 'payables_to'
  | 'invoices_to'
  | 'payable'
  | 'invoice'
  | 'counter_party'
  | 'type'
  | 'label'
  | 'commodity'
  | 'facility'
  | 'payment_terms'
  | 'order_type'
  | 'order'
  | 'counter_party_group'
  | 'reconciliation'
  | 'deposit_slip'
  | 'settlement'
  | 'remittance'
  | 'bill_slug'
  | 'payment_id'
  | 'order_id';

export type LedgerAccount = {
  id: number;
  fragmentAccountPath: string;
  accountType: AccountSubType;
  accountNumber: string;
  accountName: string;
};

export type PalletTag = {
  id: number;
  tagNumber: string;

  cartonQuantity: number;
  initialQuantity: number;

  weight: number;
  initialWeight: number;
};

export type PalletTagsOnLineItem = { quantity: number; weight?: number; palletTag: PalletTag };

export type LineItem = {
  id: number;

  enteredByWeight: boolean;
  unitsOrdered?: number;
  weightOrdered?: number;
  unitsReceived?: number;
  weightReceived: number;
  unitsPicked?: number;
  weightPicked?: number;
  unitsShipped?: number;
  weightShipped?: number;
  unitWeightCount?: number;
  settlementPrice?: number;
  settlementPriceWeight?: number;
  currencyPrice: number;
  product?: Product;
  productId?: number;
  freeformProductDescription?: string;
  lot: Partial<Lot>;
  lots?: Lot[];
  landedCost?: number;
  profit?: number;
  consignmentPayableId: number;
  parentLineItemId?: number;
  childrenLineItems?: LineItem[];
  external: boolean;
  vendor?: BusinessEntity;
  vendorId?: number;

  buyPrice: number;
  sellPrice: number;

  buyCurrencyPrice: number;
  sellCurrencyPrice: number;

  deliveredPrice: number;
  deliveredCurrencyPrice: number;

  palletTagsOnLineItem?: PalletTagsOnLineItem[];
  purchaseOrderId?: number;
  salesOrderId?: number;
  parentLineItem?: LineItem;
};

export type BuySellOrderLineItem = {
  id: number;

  unitsOrdered?: number;
  unitsPicked?: number;
  unitsShipped?: number;
  unitsInvoiced?: number;

  product: Product;

  sellPrice: number;
  sellCurrencyPrice: number;

  buyPrice: number;
  buyCurrencyPrice: number;

  deliveredPrice: number;
  deliveredCurrencyPrice: number;
};

export type SalesTerms =
  | 'COD'
  | 'NET_7'
  | 'NET_10'
  | 'NET_14'
  | 'NET_21'
  | 'NET_25'
  | 'NET_30'
  | 'NET_45'
  | 'NET_60'
  | 'NET_40'
  | 'NET_90';

export type SalesOrderStatus =
  | 'DRAFT_CUSTOMER'
  | 'DRAFT'
  | 'PENDING'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'PICKING'
  | 'SHIPPED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'ERROR';

export type Order = {
  id: number;
  slug: string;
  type: 'WORK_ORDER' | 'SALES_ORDER' | 'PURCHASE_ORDER' | 'BUY_SELL_ORDER';
  reference?: string;
  organizationId: number;

  counterParty: { businessEntityId?: number; contactId?: number; shippingAddressId?: number };
};

export type BuySellOrder = Omit<Order, 'counterParty'> & {
  customer: { businessEntityId?: number; contactId?: number; shippingAddressId?: number };
  vendor: { businessEntityId?: number; contactId?: number; shippingAddressId?: number };
};

export type Contact = { id: number; name: string; email: string; phone: string };

export type Place = {
  id: number;
  description?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  addressCountry?: string;
  faxNumber?: string;
  phone?: string;
  addressRoles?: string[];
};

export type Shipment = {
  terms: string;
  shipDate: string | Date;
  deliveryDate: string | Date;
  freightCompany?: BusinessEntity;
  freightCompanyId?: number;
  destination?: Place;
  destinationId?: number;
  origin?: Place;
  originId?: number;
  // Ensure that place is remapped to either destination or origin
  place?: Place;
  placeId?: number;
  customFields: CustomFields;
  temperatureMin: number;
  temperatureMax: number;
};

export type AccountType = 'Asset' | 'Liability' | 'Income' | 'Expense';
export type AccountSubType =
  | 'CashAssets'
  | 'CurrentAssets'
  | 'CurrentLiabilities'
  | 'DirectCosts'
  | 'Equity'
  | 'Income'
  | 'IndirectCosts'
  | 'LongTermAssets'
  | 'LongTermLiabilities'
  | 'OtherAssets'
  | 'OtherCosts'
  | 'OtherIncome'
  | 'asset'
  | 'expense'
  | 'income'
  | 'liability';

export enum AccountLevel {
  ACCOUNTS_RECEIVABLE = 'ACCOUNTS_RECEIVABLE',
  ACCOUNTS_PAYABLE = 'ACCOUNTS_PAYABLE',
  GROWER_PAYABLE = 'GROWER_PAYABLE',
}

type AnyAccountLevel = 'ACCOUNTS_RECEIVABLE' | 'ACCOUNTS_PAYABLE' | 'GROWER_PAYABLE';

export type ExpenseChargePer = 'ORDER' | 'PALLET' | 'CARTON' | 'UNIT_WEIGHT' | 'MANUAL';

export type Expense = {
  id: number;
  organizationId: number;
  expenseType: { id: number; name: string; receivablesAccount?: LedgerAccount; payablesAccount?: LedgerAccount };
  description?: string;
  unitAmount: number;
  chargePer: ExpenseChargePer;
  quantity: number;
  currencyAmount?: number;
  settlementAmount?: number;
  ledgerAccount?: LedgerAccount;
  ledgerAccountId?: number;
  businessEntity?: BusinessEntity;
  businessEntityId?: number;
  parentExpenseId?: number;
  includedInDlvPrice?: boolean;
  accountLevel: AnyAccountLevel;
};

export type Payment = {
  id: number;
  slug: string;
  paymentDate: string | Date;
  paymentAmount: number;
  paymentMethod: string;
  paymentDetails: string;
  payee?: BusinessEntity;
  payer?: BusinessEntity;
  bill?: {
    slug: string;
    invoiceDate?: string | Date;
    reference?: string;
    billingAddress?: Place;
    view?: { totalAmountDue: number };
    attachedOrders?: Array<{ purchaseOrder?: { buyer?: { user?: User } } }>;
  };
  childPayments?: Payment[];
};

export type Note = {
  id: number;
  text: string;
  membership: Membership;
  createdAt: string | Date;
  documentTypes: string[];
  customDocumentTypes: { documentTemplateId: number }[];
};

export type ListTemplate<T> = { isEmpty: boolean; items: T[] };

export type CustomFields = unknown;

export enum UnitRounding {
  TWO_DECIMALS = 'TWO_DECIMALS',
  NEAREST_QUARTER = 'NEAREST_QUARTER',
}

/** @deprecated Use UnitRounding instead */
export enum SystemRounding {
  TWO_DECIMALS = 'TWO_DECIMALS',
  NEAREST_QUARTER = 'NEAREST_QUARTER',
}

export type FinanceConfiguration = {
  id: number;
  cogsAccount?: LedgerAccount;
  consignmentExpensesAccount?: LedgerAccount;
  defaultAssetsAccount?: LedgerAccount;
  defaultExpensesAccount?: LedgerAccount;
  defaultIncomeAccount?: LedgerAccount;
  defaultLiabilitiesAccount?: LedgerAccount;
  deferredCogsAccount?: LedgerAccount;
  inventoryAccount?: LedgerAccount;
  inventoryIncomeAccount?: LedgerAccount;
  inventoryLossAccount?: LedgerAccount;
  orderSalesAccount?: LedgerAccount;
  otherExpensesAccount?: LedgerAccount;
  payableAccount?: LedgerAccount;
  purchaseAccrualsAccount?: LedgerAccount;
  purchaseExpensesAccount?: LedgerAccount;
  receivablesAccount?: LedgerAccount;
};

export type PageInfo = { endCursor?: string | null; hasNextPage?: boolean };

export type PaginationEdge<T> = { node: T; cursor: string };

export type PaginationResult<T = any> = { totalCount: number; edges: PaginationEdge<T>[]; pageInfo: PageInfo };

export type LotTransferItem = {
  id: number;
  lot: Lot;
  unitsTransferred: number;
  palletTagsOnTransferItem: PalletTagsOnLineItem[];
};

export type ProductionRunComponent = {
  id: number;
  type: 'INPUT' | 'OUTPUT';
  lot: Lot;
  quantityChange: number;
  weightDumped?: number;
  weightProduced?: number;
  palletTagInputs: PalletTagsOnLineItem[];
  palletTags: PalletTag[];
};

export type WorkOrderLineItem = {
  id: number;
  unitsOrdered: number;
  unitsReceived: number;
  weightReceived: number;
  lot: Lot;
  product: Product;
};

export type BillSourceLineItem = {
  id: number;
  parentLineItemId?: number;
  lot: { slug: string };
  product: Product;
  unitsPicked?: number;
  deletedAt?: string | Date;
};

export type BillLineItem = {
  id: number;
  description: string;
  unitCount: number;
  unitDescription: string;
  unitPrice: number;
  sourceExpense?: Expense;
  sourceLineItem: BillSourceLineItem;
  childrenLineItems?: BillSourceLineItem[];
  sourceBuySellOrderLineItem?: { product: Product; unitsInvoiced?: number; deletedAt?: string | Date };
  deletedAt?: string | Date;
};

export type BillSettlementLineItem = {
  id: number;
  description: string;
  unitCount: number;
  unitDescription: string;
  unitPrice: number;
  consignmentWeightUnitPrice?: number;
  sourceExpense?: Expense;
  sourceLineItem?: {
    salesOrder: { shipDate: string | Date; slug: string };
    settlementPrice?: number;
    settlementPriceWeight?: number;
    sellPrice: number;
    sellCurrencyPrice: number;
    lot: {
      slug: string;
      lotOrderSources?: { workOrderId: number; proportionComposedOfSource: number | Decimal }[];
      orderSources?: { workOrderId: number; proportionComposedOfSource: number | Decimal }[];
      initialQuantity: number;
      initialWeight: number;
      product: Product;
    };
    palletTagsOnLineItem: PalletTagsOnLineItem[];
    unitsPicked?: number;
    parentLineItem?: { sellPrice: number; sellCurrencyPrice: number };
  };
};

export type QboReferenceFormat = 'PRIVATE_NOTE' | 'DOC_NUMBER';

export type QboPreferences = { referenceFormat: QboReferenceFormat; syncDraftBills: boolean };

/** @deprecated Use QboPreferences instead */
export type QboConfig = QboPreferences;
